import { MarketView } from '../perps-v1-shared/types';
import { buildChildrenByParent } from '../perps-v1-shared/viewTree';

// Same rule as perps-v1-shared/api.ts's displayLabel (en override, else slug, else fallback),
// copied rather than imported: that module re-exports apiGet/apiSend, which pull in
// AppDataProvider -> AppAuthProvider -> axios's ESM build. CRA's Jest setup can't transform
// that import chain (see disableWarning.ts in perps-v1-sectors for the same workaround), so
// importing it here would make viewTree.test.ts fail to even load.
function viewLabel(view: MarketView): string {
  return view.label?.en || view.label?.slug || view.code;
}

export interface ViewTreeNode {
  view: MarketView;
  depth: number;
  // Ancestor labels root-first, including this view's own label last.
  path: string[];
}

// Depth-first tree order: each top-level view in displayOrder, then its children in
// displayOrder, recursively — up to whatever depth the tree actually has (the nav tree is
// capped at three levels elsewhere, but nothing here assumes that cap). This is what makes
// `perps -> all/hot/gainers/...` group together instead of each child's displayOrder=1
// colliding with every other parent's displayOrder=1 child in a flat sort.
//
// Cycle guard: a view is only ever visited once (`visited`), so a parentCode cycle — direct
// (A's parent is A) or indirect (A's parent is B, B's parent is A) — can't recurse forever.
// Members of a cycle never have a path down from a real root (each one's only "parent" is
// another cycle member), so the top-down walk leaves them unvisited; the second pass below
// still appends them — own label only, ordered by displayOrder — so a cyclic view stays
// reachable instead of quietly disappearing from the picker.
export function flattenViewTree(views: MarketView[]): ViewTreeNode[] {
  const childrenByParent = buildChildrenByParent(views);
  const result: ViewTreeNode[] = [];
  const visited = new Set<string>();

  const walk = (list: MarketView[] | undefined, depth: number, ancestorPath: string[]) => {
    (list ?? []).forEach((v) => {
      if (visited.has(v.code)) return;
      visited.add(v.code);
      const path = [...ancestorPath, viewLabel(v)];
      result.push({ view: v, depth, path });
      walk(childrenByParent.get(v.code), depth + 1, path);
    });
  };

  walk(childrenByParent.get(null), 0, []);

  views
    .filter((v) => !visited.has(v.code))
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .forEach((v) => {
      result.push({ view: v, depth: 0, path: [viewLabel(v)] });
    });

  return result;
}

// Plain separator, not a translated string — matches SymbolEditorDialog's ancestorPath, the
// only other place in this console that already joins a view's ancestor chain into one label
// (e.g. "TradFi › Stocks › US Stocks").
const PATH_SEPARATOR = ' › ';

export function joinViewPath(path: string[]): string {
  return path.join(PATH_SEPARATOR);
}
